import React, { useState, useEffect, Component } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../../services/api";
import { useLobby } from "../../hooks/useLobby";
import type { BrowseListingsParams } from "../../services/api/LfgApi";
import BrowseListings from "./components/BrowseListings";
import FilterControls from "./components/FilterControls";
import CreateListingForm from "./components/CreateListingForm";
import LobbyView from "./components/LobbyView/LobbyView";
import styles from "./MatchmakingPage.module.scss";

type TabId = "browse" | "create" | "mylobby";

const VALID_TABS: TabId[] = ["browse", "create", "mylobby"];

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class TabErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className={styles.errorState}>
          <p>Something went wrong.</p>
          <button
            className={styles.retryButton}
            onClick={() => this.setState({ hasError: false })}
          >
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const MatchmakingPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const lobbyState = useLobby();

  const tabParam = searchParams.get("tab") as TabId | null;
  const activeTab: TabId = tabParam && VALID_TABS.includes(tabParam) ? tabParam : "browse";

  const setActiveTab = (tab: TabId) => {
    setSearchParams({ tab });
  };

  const [filters, setFilters] = useState<BrowseListingsParams>({});
  const [games, setGames] = useState<Array<{ gameId: string; name: string }>>([]);

  useEffect(() => {
    api.games.listGames({ limit: 1000 }).then((res) => {
      setGames(res.games.map((g) => ({ gameId: g.gameId, name: g.name })));
    }).catch(() => {});
  }, []);

  const handleJoin = () => {
    lobbyState.refresh();
    setActiveTab("mylobby");
  };

  const handleCreate = () => {
    lobbyState.refresh();
    setActiveTab("mylobby");
  };

  const tabs: Array<{ id: TabId; label: string }> = [
    { id: "create", label: "Create" },
    { id: "browse", label: "Browse" },
    { id: "mylobby", label: "My Lobbies" },
  ];

  return (
    <div className={styles.matchmakingPage}>
      <div className={styles.tabs}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`${styles.tab} ${activeTab === tab.id ? styles.tabActive : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <TabErrorBoundary>
        <div className={styles.tabContent}>
          <div style={{ display: activeTab === "browse" ? "block" : "none" }}>
            <FilterControls
              filters={filters}
              onChange={setFilters}
              games={games}
            />
            <BrowseListings filters={filters} games={games} onJoin={handleJoin} />
          </div>

          <div style={{ display: activeTab === "create" ? "block" : "none" }}>
            <CreateListingForm
              games={games}
              onSuccess={handleCreate}
            />
          </div>

          <div style={{ display: activeTab === "mylobby" ? "block" : "none" }}>
            <LobbyView lobbyState={lobbyState} />
          </div>
        </div>
      </TabErrorBoundary>
    </div>
  );
};

export default MatchmakingPage;
